import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  paginatedPcmpMisQueueSchema,
  pcmpMisBatchesSchema,
  pcmpMisBatchProposalsSchema,
  pcmpMisBatchReportSchema,
  pcmpMisPublishResultSchema,
  pcmpMisQueueCountsSchema,
  pcmpMisRollbackResultSchema,
  pcmpMisStageResultSchema,
  type PaginatedPcmpMisQueue,
  type PcmpMisBatches,
  type PcmpMisBatchesParams,
  type PcmpMisBatchProposals,
  type PcmpMisBatchProposalsParams,
  type PcmpMisBatchReport,
  type PcmpMisPublishResult,
  type PcmpMisQueueCounts,
  type PcmpMisQueueParams,
  type PcmpMisRollbackPayload,
  type PcmpMisRollbackResult,
  type PcmpMisStagePayload,
  type PcmpMisStageResult,
  type PcmpMisTab,
} from '../types/pcmpMis.types';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';

export const pcmpMisQueryKeys = {
  all: () => ['pcmp-mis'] as const,
  queues: () => [...pcmpMisQueryKeys.all(), 'queue'] as const,
  queue: (params: PcmpMisQueueParams) => [...pcmpMisQueryKeys.queues(), params] as const,
  counts: (filters: PcmpQueueFilters) => [...pcmpMisQueryKeys.all(), 'counts', filters] as const,
  batches: (params: PcmpMisBatchesParams) =>
    [...pcmpMisQueryKeys.all(), 'batches', params] as const,
  batch: (batchNo: number, params: PcmpMisBatchProposalsParams) =>
    [...pcmpMisQueryKeys.all(), 'batch', batchNo, params] as const,
  report: (batchNo: number) => [...pcmpMisQueryKeys.all(), 'report', batchNo] as const,
};

export const pcmpMisQueueQueryOptions = (params: PcmpMisQueueParams) =>
  queryOptions({
    queryKey: pcmpMisQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpMisQueue> => {
      const res = await apiClient.get(API.pcmpMis.queue, { params });
      return paginatedPcmpMisQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpMisCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpMisQueryKeys.counts(filters),
    queryFn: async (): Promise<PcmpMisQueueCounts> => {
      const res = await apiClient.get(API.pcmpMis.queueCounts, { params: filters });
      return pcmpMisQueueCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpMisBatchesQueryOptions = (params: PcmpMisBatchesParams) =>
  queryOptions({
    queryKey: pcmpMisQueryKeys.batches(params),
    queryFn: async (): Promise<PcmpMisBatches> => {
      const res = await apiClient.get(API.pcmpMis.batches, { params });
      return pcmpMisBatchesSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

// Fresh on every visit: publish and rollback by other MIS users change it.
export const pcmpMisBatchQueryOptions = (batchNo: number, params: PcmpMisBatchProposalsParams) =>
  queryOptions({
    queryKey: pcmpMisQueryKeys.batch(batchNo, params),
    queryFn: async (): Promise<PcmpMisBatchProposals> => {
      const res = await apiClient.get(API.pcmpMis.batchProposals(batchNo), { params });
      return pcmpMisBatchProposalsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

export const pcmpMisBatchReportQueryOptions = (batchNo: number) =>
  queryOptions({
    queryKey: pcmpMisQueryKeys.report(batchNo),
    queryFn: async (): Promise<PcmpMisBatchReport> => {
      const res = await apiClient.get(API.pcmpMis.report(batchNo));
      return pcmpMisBatchReportSchema.parse(res.data);
    },
    staleTime: 0,
  });

export async function stagePcmpMisBatch(payload: PcmpMisStagePayload): Promise<PcmpMisStageResult> {
  const res = await apiClient.post(API.pcmpMis.batches, payload);
  return pcmpMisStageResultSchema.parse(res.data);
}

export async function publishPcmpMisBatch(
  batchNo: number,
  comment: string,
): Promise<PcmpMisPublishResult> {
  const res = await apiClient.post(API.pcmpMis.publish(batchNo), { comment });
  return pcmpMisPublishResultSchema.parse(res.data);
}

export async function rollbackPcmpMisBatch(
  batchNo: number,
  payload: PcmpMisRollbackPayload,
): Promise<PcmpMisRollbackResult> {
  const res = await apiClient.post(API.pcmpMis.rollback(batchNo), payload);
  return pcmpMisRollbackResultSchema.parse(res.data);
}

/** The API names the file the same way; its header is not readable cross-origin. */
export function pcmpBatchXlsxFilename(batchNo: number, date: string): string {
  return `CMP_Disbursement_Batch_${batchNo}_${date}.xlsx`;
}

export function downloadPcmpMisBatchXlsx(batchNo: number, date: string): Promise<void> {
  return downloadApiFile(API.pcmpMis.exportXlsx(batchNo), pcmpBatchXlsxFilename(batchNo, date));
}

export function pcmpMisQueueXlsxFilename(tab: PcmpMisTab, date: string): string {
  return `CMP_MIS_${tab}_${date}.xlsx`;
}

/** A queue tab, under the same filters, as a CMP disbursement workbook (no paging). */
export function downloadPcmpMisQueueXlsx(
  tab: PcmpMisTab,
  filters: PcmpQueueFilters,
  date: string,
): Promise<void> {
  return downloadApiFile(API.pcmpMis.queueXlsx, pcmpMisQueueXlsxFilename(tab, date), {
    ...filters,
    tab,
  });
}
